/**
 * Sheds a service worker left behind by the single-app build.
 *
 * **This is an upgrade hazard, not a tidy-up.** Until the split, the webmail registered its worker at
 * `/sw.js`, which scoped it to the whole origin: it intercepted every navigation on the host and
 * answered offline ones from a cached `/index.html`. That registration lives in the browsers of
 * everybody who has opened the webmail, and it survives the upgrade — the new build does not replace
 * it, because nothing is served at `/sw.js` any more. The browser's update check fetches that URL,
 * gets the admin panel's HTML, fails the update, and **keeps running the old worker**.
 *
 * Left alone, a returning user gets a stale shell in front of both applications, with no error and no
 * way to reason about it from the outside. So each app asks, on boot, whether any registration is
 * scoped wider than itself, and drops it.
 *
 * Safe to keep indefinitely and cheap to run: on a browser with nothing stale it resolves to an empty
 * list. It is in `web-shared` because both apps have to do it — the admin panel never registered a
 * worker and is still served one.
 */
export async function dropStaleWorkers(ownScope?: string): Promise<number> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return 0;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    // No `ownScope` means this app has no worker of its own, so every registration here is somebody
    // else's — which is the admin panel's case exactly.
    const stale = regs.filter((r) => new URL(r.scope).pathname !== ownScope);
    await Promise.all(stale.map((r) => r.unregister()));
    // The caches outlive the registration that filled them, and the old shell cache holds the
    // pre-split `index.html` — the exact thing that would be served in place of either app.
    if (stale.length && typeof caches !== 'undefined') {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    }
    return stale.length;
  } catch {
    // A browser that refuses to enumerate registrations leaves the stale worker in place; there is
    // nothing better to do from here, and failing boot over it would be worse than a stale shell.
    return 0;
  }
}
